/**
 * GoalListScreen (Web Version)
 * Manage all goals (main and side) without navigation
 * Inline editing and deletion
 * Uses regular FlatList (no drag support on web)
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  TextInput,
  Alert,
  Platform,
  FlatList,
} from 'react-native';
import { GoalListScreenProps } from '../navigation/types';
import { useGoals } from '../hooks/useGoals';
import { Goal } from '../models/Goal';

export const GoalListScreen: React.FC<GoalListScreenProps> = ({ navigation }) => {
  const { goals, currentAccountId, updateGoalSafe, deleteGoalSafe } = useGoals();
  const [editingGoalId, setEditingGoalId] = useState<string | null>(null);
  const [editedName, setEditedName] = useState('');

  const handleStartEdit = (goal: Goal) => {
    setEditingGoalId(goal.id);
    setEditedName(goal.name);
  };

  const handleSaveEdit = async (goalId: string) => {
    if (editedName.trim()) {
      await updateGoalSafe(goalId, { name: editedName.trim() });
      setEditingGoalId(null);
      setEditedName('');
    }
  };

  const handleCancelEdit = () => {
    setEditingGoalId(null);
    setEditedName('');
  };

  const handleDelete = (goal: Goal) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${goal.name}"? This will delete all completion history for this goal.`
    );
    if (confirmed) {
      deleteGoalSafe(goal.id);
    }
  };

  const renderGoalRow = ({ item }: { item: Goal }) => {
    const isEditing = editingGoalId === item.id;
    const isMainGoal = item.type === 'main';

    return (
      <View style={[styles.goalRow, isMainGoal && styles.mainGoalRow]}>
        <View style={styles.goalRowContent}>
          {/* Goal Type Badge */}
          <View style={[styles.typeBadge, isMainGoal ? styles.mainBadge : styles.sideBadge]}>
            <Text style={styles.typeBadgeText}>
              {isMainGoal ? 'MAIN' : 'DAILY ACTION'}
            </Text>
          </View>

          {/* Goal Content */}
          <View style={styles.goalContent}>
            {isEditing ? (
              <View style={styles.editContainer}>
                <TextInput
                  style={styles.editInput}
                  value={editedName}
                  onChangeText={setEditedName}
                  autoFocus
                  placeholder="Goal name"
                />
                <View style={styles.editActions}>
                  <TouchableOpacity
                    style={styles.saveButton}
                    onPress={() => handleSaveEdit(item.id)}
                  >
                    <Text style={styles.saveButtonText}>Save</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.cancelButton}
                    onPress={handleCancelEdit}
                  >
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              <>
                <Text style={[styles.goalName, isMainGoal && styles.mainGoalName]}>
                  {item.name}
                </Text>
                <Text style={styles.goalMeta}>
                  Created {new Date(item.createdAt).toLocaleDateString()}
                </Text>
              </>
            )}
          </View>

          {/* Action Buttons */}
          {!isEditing && (
            <View style={styles.actions}>
              <TouchableOpacity
                style={styles.editButton}
                onPress={() => handleStartEdit(item)}
              >
                <Text style={styles.editButtonText}>Edit</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.deleteButton}
                onPress={() => handleDelete(item)}
              >
                <Text style={styles.deleteButtonText}>Delete</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    );
  };

  const currentAccountGoals = goals.filter(g => g.accountId === currentAccountId);
  const mainGoals = currentAccountGoals.filter(g => g.type === 'main');
  const dailyActions = currentAccountGoals.filter(g => g.type === 'side');

  // Combine main goals and daily actions for display
  const allGoalsForDisplay = [...mainGoals, ...dailyActions];

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Manage Goals</Text>
        <Text style={styles.headerSubtitle}>
          {mainGoals.length} main • {dailyActions.length} daily actions
        </Text>
      </View>

      <FlatList
        data={allGoalsForDisplay}
        renderItem={renderGoalRow}
        keyExtractor={item => item.id}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No goals yet</Text>
            <Text style={styles.emptySubtext}>Create a goal to get started</Text>
          </View>
        }
      />

      <View style={styles.footer}>
        <TouchableOpacity
          style={styles.createButton}
          onPress={() => navigation.navigate('CreateGoal', { type: 'side' })}
        >
          <Text style={styles.createButtonText}>+ Add Daily Action</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    backgroundColor: '#fff',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#666',
  },
  list: {
    flex: 1,
  },
  listContent: {
    padding: 16,
  },
  goalRow: {
    backgroundColor: '#fff',
    borderRadius: 8,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#34C759',
  },
  goalRowContent: {
    flex: 1,
    padding: 16,
  },
  mainGoalRow: {
    borderLeftColor: '#007AFF',
  },
  typeBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    marginBottom: 8,
  },
  mainBadge: {
    backgroundColor: '#E3F2FF',
  },
  sideBadge: {
    backgroundColor: '#E8F5E9',
  },
  typeBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  goalContent: {
    flex: 1,
    marginBottom: 12,
  },
  goalName: {
    fontSize: 17,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  mainGoalName: {
    fontSize: 18,
    color: '#007AFF',
  },
  goalMeta: {
    fontSize: 12,
    color: '#999',
  },
  editContainer: {
    width: '100%',
  },
  editInput: {
    fontSize: 16,
    padding: 12,
    backgroundColor: '#f9f9f9',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    marginBottom: 8,
  },
  editActions: {
    flexDirection: 'row',
    gap: 8,
  },
  saveButton: {
    flex: 1,
    backgroundColor: '#007AFF',
    padding: 10,
    borderRadius: 6,
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 10,
    borderRadius: 6,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
  },
  editButton: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 10,
    borderRadius: 6,
    alignItems: 'center',
  },
  editButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#007AFF',
  },
  deleteButton: {
    flex: 1,
    backgroundColor: '#FFE5E5',
    padding: 10,
    borderRadius: 6,
    alignItems: 'center',
  },
  deleteButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FF3B30',
  },
  emptyContainer: {
    padding: 32,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
  },
  footer: {
    padding: 16,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  createButton: {
    backgroundColor: '#34C759',
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  createButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
});
